import React, { useState, useMemo, useEffect } from 'react';
import { Box, Button, TextField, CircularProgress, Alert, MenuItem } from '@mui/material';
import { useHistory } from 'react-router-dom';
import { ColumnDirective, ColumnsDirective, GridComponent } from '@syncfusion/ej2-react-grids';
import { Inject, Page } from '@syncfusion/ej2-react-grids';
import { useRateCard } from './useRateCard';
import BaseRateManagerDialog from './ManageBaseRateCard';
import HaulageCategoryManagerDialog from './ManageHaulageCategory';

// ─── Parameter defaults ───────────────────────────────────────────────────────

const defaultParams = {
  fuelCurrentRate: 175,
  fuelBaseRate: 120,
  baseRatePercent: 100,
  subContractorRate: 0.7,
  tonnesPerLoad: 28,
  rateChangePercent: 0,
  // Haulage — "Standard" means both adjustments are 0
  haulageCategory: 'standard', // holds the selected id or 'standard'
  priceChangePCT: 0,
  priceChangeAdj: 0,
};

// ─── Shared grid styles ───────────────────────────────────────────────────────

const GRID_STYLES = `
  .e-grid .e-selectionbackground { background-color: #D6EAF8 !important; }
  .e-grid .e-headercell.gap-column,
  .e-grid .e-rowcell.gap-column  { border: none !important; background: transparent !important; }

  .e-grid .e-headercell.col-actual-rate { background-color: #E8F5E9 !important; }
  .e-grid .e-headercell.col-actual-rate .e-headercelldiv { color: #1B5E20 !important; font-weight: 700 !important; }
  .e-grid .e-rowcell.col-actual-rate    { background-color: #E8F5E9 !important; }

  .e-grid .e-headercell.col-per-load { background-color: #E8F5E9 !important; }
  .e-grid .e-headercell.col-per-load .e-headercelldiv { color: #1B5E20 !important; font-weight: 700 !important; }
  .e-grid .e-rowcell.col-per-load    { background-color: #E8F5E9 !important; }
`;

// ─── Parameters Panel ────────────────────────────────────────────────────────

const ParametersPanel = ({ params, onChange, onCalculate, loading, haulageCategories }) => {
  const numberFields = [
    { key: 'fuelCurrentRate', label: 'Current Fuel Rate', step: '1' },
    { key: 'fuelBaseRate', label: 'Base Fuel Rate', step: '1', disabled: true },
    { key: 'baseRatePercent', label: 'Inflation (%)', step: '1' },
    { key: 'subContractorRate', label: 'Sub Contractor Rate', step: '0.01' },
    { key: 'tonnesPerLoad', label: 'Tons Per Load', step: '1' },
    { key: 'rateChangePercent', label: 'Rate Change (%)', step: '1' },
  ];

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        p: 2,
        mb: 3,
        backgroundColor: 'background.paper',
      }}>
      <Box display="flex" flexWrap="wrap" alignItems="center" gap={2}>
        {numberFields.map(({ key, label, step, disabled }) => (
          <TextField
            key={key}
            size="small"
            label={label}
            type="number"
            disabled={disabled}
            inputProps={{ step }}
            value={params[key]}
            onChange={e => onChange(key, e.target.value)}
            sx={{ width: 155 }}
          />
        ))}

        {/* Haulage Category — wider than the numeric fields */}
        <TextField
          select
          size="small"
          label="Haulage Category"
          value={params.haulageCategory}
          sx={{ width: 240 }}
          onChange={e => {
            const selectedId = e.target.value;
            if (selectedId === 'standard') {
              onChange('haulageCategory', 'standard');
              onChange('priceChangePCT', 0);
              onChange('priceChangeAdj', 0);
            } else {
              const found = haulageCategories.find(c => String(c.id) === String(selectedId));
              onChange('haulageCategory', selectedId);
              onChange('priceChangePCT', found?.priceChangePCT ?? 0);
              onChange('priceChangeAdj', found?.priceChangeAdj ?? 0);
            }
          }}>
          <MenuItem value="standard">Standard (default)</MenuItem>
          {haulageCategories.map(c => (
            <MenuItem key={c.id} value={String(c.id)}>
              {c.haulageCategory}
              {c.priceChangePCT ? `  (${c.priceChangePCT > 0 ? '+' : ''}${c.priceChangePCT}%)` : ''}
              {c.priceChangeAdj ? `  (€${c.priceChangeAdj})` : ''}
            </MenuItem>
          ))}
        </TextField>

        <Button
          variant="contained"
          onClick={onCalculate}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}>
          {loading ? 'Calculating…' : 'Calculate'}
        </Button>
      </Box>
    </Box>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const RateCard = () => {
  const history = useHistory();
  const {
    rateCards,
    loading,
    error,
    calculate,
    baseRates,
    fetchBaseRates,
    addBaseRate,
    updateBaseRate,
    deleteBaseRate,
    haulageCategories,
    fetchHaulageCategories,
    updateHaulageCategory,
    fetchParams,
    updateParams,
  } = useRateCard();

  const [params, setParams] = useState(defaultParams);
  const [isBaseRateModalOpen, setBaseRateModal] = useState(false);
  const [isHaulageModalOpen, setHaulageModal] = useState(false);

  // Fetch haulage categories once on mount so the dropdown is populated immediately
  useEffect(() => {
    fetchHaulageCategories();
  }, [fetchHaulageCategories]);

  useEffect(() => {
    const init = async () => {
      const savedParams = await fetchParams();
      const merged = savedParams ? { ...defaultParams, ...savedParams } : defaultParams;

      setParams(merged);
      // Pass merged directly — don't rely on state being updated yet
      await calculate({
        fuelCurrentRate: Number(merged.fuelCurrentRate),
        fuelBaseRate: Number(merged.fuelBaseRate),
        baseRatePercent: Number(merged.baseRatePercent),
        subContractorRate: Math.round(Number(merged.subContractorRate) * 100),
        tonnesPerLoad: Number(merged.tonnesPerLoad),
        rateChangePercent: Number(merged.rateChangePercent),
        haulageCategoryId: merged.haulageCategory === 'standard' ? 0 : merged.haulageCategory,
        priceChangePCT: Number(merged.priceChangePCT),
        priceChangeAdj: Number(merged.priceChangeAdj),
      });
    };
    init();
  }, []); // fetchHaulageCategories already has its own useEffect

  const handleParamChange = (key, value) => setParams(prev => ({ ...prev, [key]: value }));

  const handleCalculate = async () => {
    await Promise.all([
      calculate({
        fuelCurrentRate: Number(params.fuelCurrentRate),
        fuelBaseRate: Number(params.fuelBaseRate),
        baseRatePercent: Number(params.baseRatePercent),
        subContractorRate: Math.round(Number(params.subContractorRate) * 100),
        tonnesPerLoad: Number(params.tonnesPerLoad),
        rateChangePercent: Number(params.rateChangePercent),
        haulageCategoryId: params.haulageCategory === 'standard' ? 0 : params.haulageCategory,
        priceChangePCT: Number(params.priceChangePCT),
        priceChangeAdj: Number(params.priceChangeAdj),
      }),
      updateParams(params),
    ]);
  };

  const pageSettings = useMemo(() => ({ pageSize: 30, pageSizes: [5, 10, 20, 30, 40] }), []);

  // ── Column templates ──────────────────────────────────────────────────────

  const distanceTemplate = row => `${row.startKm} – ${row.endKM} km`;
  const actualRateTemplate = row => (
    <span style={{ color: '#1B5E20', fontWeight: 700 }}>€{Number(row.actual_Rate ?? 0).toFixed(2)}</span>
  );
  const subContractTemplate = row => `€${Number(row.scRate ?? 0).toFixed(2)}`;
  const perLoadTemplate = row => (
    <span style={{ color: '#1B5E20', fontWeight: 700 }}>€{Number(row.perLoad ?? 0).toFixed(2)}</span>
  );
  const fuelSurchargeTemplate = row => `€${Number(row.fuelSurCharge ?? 0).toFixed(2)}`;
  const altPricingTemplate = row => `€${Number(row.alternativePricing ?? 0).toFixed(2)}`;

  const actualRateAttrs = { class: 'col-actual-rate' };
  const perLoadAttrs = { class: 'col-per-load' };

  return (
    <div className="app-wrapper">
      <style>{GRID_STYLES}</style>

      {/* ── Page header ──────────────────────────────────────────────────── */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <h2>Manage Rate Card</h2>
        <Box display="flex" gap={2}>
          <Button variant="contained" onClick={() => setHaulageModal(true)}>
            Manage Haulage Categories
          </Button>
          <Button variant="contained" onClick={() => setBaseRateModal(true)}>
            Change Base Rate
          </Button>
          <Button variant="outlined" onClick={() => history.push('/')}>
            Home
          </Button>
        </Box>
      </Box>

      {/* ── Params panel ─────────────────────────────────────────────────── */}
      <ParametersPanel
        params={params}
        onChange={handleParamChange}
        onCalculate={handleCalculate}
        loading={loading}
        haulageCategories={haulageCategories}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* ── Grid ─────────────────────────────────────────────────────────── */}
      {console.log('RATE CARDS', rateCards)}
      <div style={{ height: 'calc(100vh - 150px)' }}>
        <GridComponent
          dataSource={rateCards}
          filterSettings={{ type: 'Excel' }}
          allowResizing
          allowReordering
          allowTextWrap
          pageSettings={pageSettings}
          toolbar={['Search']}
          height={'100%'}>
          <ColumnsDirective>
            <ColumnDirective
              field="startKm"
              headerText="Distance (km)"
              width="150"
              textAlign="Center"
              template={distanceTemplate}
              isPrimaryKey
            />
            <ColumnDirective
              field="actual_Rate"
              headerText="Actual Rate"
              customAttributes={actualRateAttrs}
              width="130"
              textAlign="Center"
              template={actualRateTemplate}
            />
            <ColumnDirective
              field="scRate"
              headerText="Sub Contract"
              width="130"
              textAlign="Center"
              template={subContractTemplate}
            />
            <ColumnDirective
              width="30"
              customAttributes={{ class: 'gap-column' }}
              template={() => <></>}
              allowFiltering={false}
              allowSorting={false}
              allowResizing={false}
            />
            <ColumnDirective
              field="perLoad"
              headerText="Per Load"
              customAttributes={perLoadAttrs}
              width="130"
              textAlign="Center"
              template={perLoadTemplate}
            />
            <ColumnDirective
              field="fuelSurCharge"
              headerText="Fuel Surcharge"
              width="140"
              textAlign="Center"
              template={fuelSurchargeTemplate}
            />
            <ColumnDirective
              field="alternativePricing"
              headerText="Alternate Pricing"
              width="150"
              textAlign="Center"
              template={altPricingTemplate}
            />
          </ColumnsDirective>
          <Inject services={[Page]} />
        </GridComponent>
      </div>

      {/* ── Dialogs ──────────────────────────────────────────────────────── */}
      <BaseRateManagerDialog
        open={isBaseRateModalOpen}
        onClose={() => setBaseRateModal(false)}
        baseRates={baseRates}
        fetchBaseRates={fetchBaseRates}
        addBaseRate={addBaseRate}
        updateBaseRate={updateBaseRate}
        deleteBaseRate={deleteBaseRate}
      />

      <HaulageCategoryManagerDialog
        open={isHaulageModalOpen}
        onClose={() => setHaulageModal(false)}
        haulageCategories={haulageCategories}
        fetchHaulageCategories={fetchHaulageCategories}
        updateHaulageCategory={updateHaulageCategory}
      />
    </div>
  );
};

export default RateCard;
